import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mail, Award, Building2, Image, Upload } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import ProfilePhotoUpload from '../ui/ProfilePhotoUpload';
import { useCurrentDojo } from '@/hooks/useCurrentDojo';

interface AdminProfileFormProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const AdminProfileForm: React.FC<AdminProfileFormProps> = ({ isLoading, setIsLoading }) => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'forms', 'common']);
  const { profile, updateProfile } = useAuth();
  const { dojo: currentDojo } = useCurrentDojo();
  const [beltLevels, setBeltLevels] = useState<any[]>([]);

  // State for dojo data, including logo_url
  const [dojoData, setDojoData] = useState({
    logo_url: currentDojo?.logo_url || '',
    name: currentDojo?.name || '',
    address: currentDojo?.address || '',
    phone: currentDojo?.phone || '',
    email: currentDojo?.email || '',
    website: currentDojo?.website || '',
    description: currentDojo?.description || ''
  });

  const [profileData, setProfileData] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    belt_level_id: profile?.belt_level_id || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (profile) {
      setProfileData(prev => ({
        ...prev,
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address: profile.address || '',
        belt_level_id: profile.belt_level_id || ''
      }));
    }
  }, [profile]);

  // Update dojoData when currentDojo changes
  useEffect(() => {
    if (currentDojo) {
      setDojoData({
        logo_url: currentDojo.logo_url || '',
        name: currentDojo.name || '',
        address: currentDojo.address || '',
        phone: currentDojo.phone || '',
        email: currentDojo.email || '',
        website: currentDojo.website || '',
        description: currentDojo.description || ''
      });
    }
  }, [currentDojo]);


  useEffect(() => {
    const fetchBeltLevels = async () => {
      if (!profile?.dojo_id) return;

      try {
        const { data, error } = await supabase
          .from('belt_levels')
          .select('*')
          .eq('dojo_id', profile.dojo_id)
          .order('order_sequence');

        if (error) {
          console.error('Error fetching belt levels:', error);
          return;
        }

        setBeltLevels(data || []);
      } catch (error) {
        console.error('Error fetching belt levels:', error);
      }
    };

    fetchBeltLevels();
  }, [profile?.dojo_id]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (profileData.newPassword && profileData.newPassword !== profileData.confirmPassword) {
      toast({
        title: t('forms:validation.passwordMismatch'),
        description: t('forms:validation.passwordMatch'),
        variant: "destructive"
      });
      return;
    }

    if (profileData.newPassword && profileData.newPassword.length < 6) {
      toast({
        title: t('forms:validation.password'),
        description: t('forms:validation.minLength', { min: 6 }),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Update profile data
      const result = await updateProfile({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        belt_level_id: profileData.belt_level_id || null
      });

      if (!result.success) {
        toast({
          title: uiText("Profile Update Failed"),
          description: result.error || "Failed to update profile.",
          variant: "destructive"
        });
        return;
      }

      // Update password if provided
      if (profileData.newPassword) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: profileData.newPassword
        });

        if (passwordError) {
          toast({
            title: uiText("Password Update Failed"),
            description: passwordError.message,
            variant: "destructive"
          });
          return;
        }
      }

      toast({
        title: uiText("Profile Updated"),
        description: uiText("Your profile has been successfully updated.")
      });

      // Clear password fields
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      toast({
        title: uiText("Error"),
        description: uiText("An unexpected error occurred."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpdate = async (photoUrl: string | null) => {
    await updateProfile({ profile_photo_url: photoUrl });
  };

  // Handler for dojo data updates, including logo
  const handleDojoUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('dojos')
        .update({
          name: dojoData.name,
          address: dojoData.address,
          phone: dojoData.phone,
          email: dojoData.email,
          website: dojoData.website,
          description: dojoData.description,
          logo_url: dojoData.logo_url
        })
        .eq('id', currentDojo?.id!);

      if (error) {
        toast({
          title: uiText("Dojo Update Failed"),
          description: error.message || "Failed to update dojo information.",
          variant: "destructive"
        });
      } else {
        toast({
          title: uiText("Dojo Updated"),
          description: uiText("Your dojo information has been successfully updated.")
        });
        // Optionally refresh currentDojo context or refetch data
      }
    } catch (error) {
      toast({
        title: uiText("Error"),
        description: uiText("An unexpected error occurred while updating dojo."),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{uiText("Update Profile Information")}</CardTitle>
            <CardDescription>
              {uiText("Change your personal information and password")}</CardDescription>
          </div>
          {currentDojo?.join_code && (
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <Badge variant="outline" className="font-mono text-sm">
                {currentDojo.join_code}
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Profile Photo Section */}
        <div className="flex justify-center mb-6 p-4 border-b">
          <div className="text-center">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{uiText("Profile Photo")}</h3>
            <ProfilePhotoUpload
              currentPhotoUrl={profile?.profile_photo_url}
              onPhotoUpdate={handlePhotoUpdate}
              size="lg"
            />
          </div>
        </div>

        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="firstName">{t('forms:personalInfo.firstName')}</Label>
              <Input
                id="firstName"
                value={profileData.first_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="lastName">{t('forms:personalInfo.lastName')}</Label>
              <Input
                id="lastName"
                value={profileData.last_name}
                onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="adminEmail">{t('forms:personalInfo.email')}</Label>
            <Input
              id="adminEmail"
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="adminPhone">{t('forms:personalInfo.phone')}</Label>
              <Input
                id="adminPhone"
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="adminAddress">{uiText("Address")}</Label>
              <Input
                id="adminAddress"
                value={profileData.address}
                onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="beltLevel" className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              {uiText("My Belt Level")}</Label>
            <Select
              value={profileData.belt_level_id || "none"}
              onValueChange={(value) => setProfileData(prev => ({ ...prev, belt_level_id: value === "none" ? "" : value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={uiText("Select your belt level")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{uiText("No belt assigned")}</SelectItem>
                {beltLevels.map((belt) => (
                  <SelectItem key={belt.id} value={belt.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: belt.color || '#9CA3AF' }}
                      />
                      {belt.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="newPassword">{uiText("New Password (optional)")}</Label>
            <Input
              id="newPassword"
              type="password"
              value={profileData.newPassword}
              onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
              placeholder={uiText("Leave blank to keep current password")}
              minLength={6}
            />
          </div>

          {profileData.newPassword && (
            <div>
              <Label htmlFor="confirmPassword">{uiText("Confirm New Password")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={profileData.confirmPassword}
                onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                required
                minLength={6}
              />
            </div>
          )}

          <Button
            type="submit"
            className="bg-red-900 hover:bg-red-800"
            disabled={isLoading}
          >
            <Mail className="h-4 w-4 mr-2" />
            {isLoading ? uiText("Updating...") : uiText("Update Profile")}
          </Button>
        </form>
      </CardContent>

      {/* Dojo Settings Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{uiText("Dojo Settings")}</CardTitle>
              <CardDescription>
                {uiText("Manage your dojo's information")}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Logo Upload Section */}
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 p-3 sm:p-6">
              <CardTitle className="text-purple-800 flex items-center gap-2 text-base sm:text-lg">
                <Image className="h-4 w-4 sm:h-5 sm:w-5" />
                {uiText("Dojo Logo")}</CardTitle>
              <CardDescription className="text-purple-600 text-sm">
                {uiText("Upload and manage your dojo's logo")}</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <form onSubmit={handleDojoUpdate} className="space-y-4">
                <div className="space-y-4">
                  <ProfilePhotoUpload
                    currentPhotoUrl={dojoData.logo_url}
                    onPhotoUpdate={(url) => setDojoData(prev => ({ ...prev, logo_url: url }))}
                    uploadPath="dojo-logos"
                    className="w-32 h-32"
                  />
                  <p className="text-sm text-muted-foreground">
                    {uiText("Recommended: Square image, minimum 200x200px, maximum 2MB")}</p>
                </div>

                {/* Dojo Name */}
                <div>
                  <Label htmlFor="dojoName">{uiText("Dojo Name")}</Label>
                  <Input
                    id="dojoName"
                    value={dojoData.name}
                    onChange={(e) => setDojoData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>

                {/* Dojo Address */}
                <div>
                  <Label htmlFor="dojoAddress">{uiText("Address")}</Label>
                  <Input
                    id="dojoAddress"
                    value={dojoData.address}
                    onChange={(e) => setDojoData(prev => ({ ...prev, address: e.target.value }))}
                  />
                </div>

                {/* Dojo Phone */}
                <div>
                  <Label htmlFor="dojoPhone">{uiText("Phone")}</Label>
                  <Input
                    id="dojoPhone"
                    value={dojoData.phone}
                    onChange={(e) => setDojoData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>

                {/* Dojo Email */}
                <div>
                  <Label htmlFor="dojoEmail">{uiText("Email")}</Label>
                  <Input
                    id="dojoEmail"
                    type="email"
                    value={dojoData.email}
                    onChange={(e) => setDojoData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>

                {/* Dojo Website */}
                <div>
                  <Label htmlFor="dojoWebsite">{uiText("Website")}</Label>
                  <Input
                    id="dojoWebsite"
                    value={dojoData.website}
                    onChange={(e) => setDojoData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>

                {/* Dojo Description */}
                <div>
                  <Label htmlFor="dojoDescription">{uiText("Description")}</Label>
                  <Textarea
                    id="dojoDescription"
                    value={dojoData.description}
                    onChange={(e) => setDojoData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <Button
                  type="submit"
                  className="bg-purple-900 hover:bg-purple-800"
                  disabled={isLoading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {isLoading ? uiText("Saving Dojo Info...") : uiText("Save Dojo Information")}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-6">
              <CardTitle className="text-blue-800 flex items-center gap-2 text-base sm:text-lg">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                {t('admin:dojoSettings.title')}
              </CardTitle>
              <CardDescription className="text-blue-600 text-sm">
                {t('admin:dojoSettingsDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              {/* Content for dojo settings if any other than logo and basic info */}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </Card>
  );
};

export default AdminProfileForm;
